import React from "react";
import { FloorMap } from "@webV2/map/MapView/FloorItem/FloorMap";
import { IResource } from "@gettactic/api";
import { mergeRefs } from "@/lib/helpers/dom/mergeRef";
import { useBooleanState } from "@gettactic/helpers/src";
import {
  useSvgMapInteractions,
  UseSvgMapInteractionsReturn
} from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import { useMap } from "@webV2/map/MapProvider";
import { useInitialMapTransform } from "@webV2/map/MapView/useInitialMapTransform";
import { MapViewSvgMiniMap } from "@webV2/map/MapView/MapViewSvg/MapViewSvgMiniMap";
import { useReserve } from "@webV2/providers/ReserveProvider/context";

export interface MapViewSvgFloorItemProps {
  floor: IResource;
  floorContainerRef: React.RefObject<HTMLDivElement>;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  index: number;
  isVisible?: boolean;
  mapRef?: React.RefObject<HTMLDivElement | null>;
  onMapReady:
    | ((
        floor: IResource | null,
        svg: SVGSVGElement,
        mapInteractions: UseSvgMapInteractionsReturn
      ) => void)
    | undefined;
  onMapReadyEditing:
    | ((
        floor: IResource | null,
        svg: SVGSVGElement,
        mapInteractions: UseSvgMapInteractionsReturn
      ) => void)
    | undefined;
  isMapEditing?: boolean;
  showMiniMap?: boolean;
  allowInteractionsOnFocussedResource?: boolean;
}

export const MapViewSvgFloorItem: React.FC<
  React.PropsWithChildren<MapViewSvgFloorItemProps>
> = ({
  floor,
  index,
  children,
  isVisible,
  mapRef,
  floorContainerRef,
  mapContainerRef,
  onMapReady,
  onMapReadyEditing,
  isMapEditing,
  showMiniMap = true,
  allowInteractionsOnFocussedResource = false
}) => {
  const {
    events,
    allResources,
    locateResourceId,
    setLocateResourceId,
    onFloorReady,
    isOnReservationModal,
    isReservationCardIsOpen
  } = useMap();

  const { isReserveOpen } = useReserve();

  const self = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const mapOriginalBox = React.useRef<DOMRect | null>(null);
  const isSvgLoaded = useBooleanState();
  const [loadingIterator, setLoadingIterator] = React.useState(0);

  const svgMapInteractions = useSvgMapInteractions(
    svgRef,
    self,
    floorContainerRef,
    {
      allowInteractionsWhenFocussed:
        allowInteractionsOnFocussedResource || isReservationCardIsOpen,
      enabled: isSvgLoaded.state
    }
  );

  const mapTransform = useInitialMapTransform({
    itemWrapperRef: self,
    isSvgLoaded: isSvgLoaded.state,
    floorContainerRef,
    mapContainerRef
  });

  React.useEffect(() => {
    if (isSvgLoaded.state && self.current) {
      const svgElement = self.current.querySelector(".injected-svg") as
        | SVGSVGElement
        | undefined;
      //if (svgElement) onMapReady?.(floor, svgElement, svgMapInteractions);
    }
  }, [isSvgLoaded.state, self.current]);

  React.useEffect(() => {
    if (
      isSvgLoaded.state &&
      locateResourceId &&
      svgMapInteractions.updateCount
    ) {
      const resource = (allResources || []).find(
        (res) => res.id === locateResourceId
      );
      if (!resource) {
        console.warn(`Resource with ID ${locateResourceId} not found.`);
        return;
      }

      // Only handle locate on the floor that actually owns this resource.
      // This avoids races where the previous floor consumes the locate signal
      // before the new floor has mounted.
      if (resource.parent_id && resource.parent_id !== floor.id) {
        return;
      }

      // Skip zoom when in reservation modal or when actively reserving
      if (isOnReservationModal || isReserveOpen || isReservationCardIsOpen) {
        // Still trigger locate event without zooming
        events.trigger("onLocateResource", resource);
        setLocateResourceId(null);
        return;
      }

      setTimeout(() => {
        svgMapInteractions.zoomToResource(resource?.id, {
          onComplete: () => {
            // highlight and locate the resource
            // events.trigger("onHighlightResource", resource);

            // locate the resource with schedules modal
            events.trigger("onLocateResource", resource);
          }
        });
      }, 500);
      setLocateResourceId(null); // Reset after zooming
    }
  }, [
    locateResourceId,
    isSvgLoaded.state,
    svgMapInteractions.updateCount,
    events,
    allResources,
    setLocateResourceId,
    isOnReservationModal,
    isReserveOpen,
    isReservationCardIsOpen
  ]);

  React.useEffect(() => {
    const onFocusResource = (resource: IResource | null, options?: { noZoom?: boolean }) => {
      // Skip all focus/zoom behavior when:
      // 1. In reservation modal (isOnReservationModal)
      // 2. OR when actively making a reservation on the map page (isReserveOpen)
      // 3. OR when reservation card is open (isReservationCardIsOpen)
      // 4. OR when explicitly requested via options
      if (isOnReservationModal || isReserveOpen || isReservationCardIsOpen || options?.noZoom) {
        return;
      }

      if (!resource) {
        svgMapInteractions.blurResource();
        return;
      }

      svgMapInteractions.focusResource(resource?.id);
    };

    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [events, svgMapInteractions, isOnReservationModal, isReserveOpen, isReservationCardIsOpen]);

  const svgStyle = {
    display: "block",
    borderRadius: 8,
    cursor: "default"
  };

  const itemStyle: React.HTMLAttributes<HTMLDivElement>["style"] = {
    transformOrigin: "top left",
    perspectiveOrigin: "center center",
    position: "absolute",
    left: 0,
    boxSizing: "border-box",
    transformStyle: "preserve-3d",
    transform: `rotateX(0deg) translate(${String(mapTransform?.translateX)}px, ${String(mapTransform?.translateY)}px) scale(${mapTransform?.scale}) translateZ(0px)`,
    //transition: "transform 0.4s ease, opacity 0.4s ease",
    pointerEvents: "auto",
    padding: 0,
    opacity: 1
  };

  // Reset the state when the map is not visible
  React.useEffect(() => {
    if (!isVisible) isSvgLoaded.false();
  }, [isVisible]);

  if (!isVisible) return null;
  return (
    <>
      <div
        className={"item-wrapper"}
        ref={mergeRefs([mapRef || null, self])}
        key={floor.id}
        style={itemStyle}
      >
        {/* MAP */}
        <FloorMap
          isEditing={isMapEditing}
          style={svgStyle}
          floor={floor}
          key={
            isMapEditing
              ? `url_${floor.id}_${index}`
              : `url_${floor.id}_${index}_editing`
          }
          onMapLoaded={(_, svg) => {
            if (!svg) return;
            svgRef.current = svg;
            isSvgLoaded.true();
            svgMapInteractions.setMapReady();
            onMapReady?.(floor, svg, svgMapInteractions);
            // Notify MapProvider that this floor's SVG is ready so it can
            // complete any pending locate/zoom flow without extra timers.
            onFloorReady(floor.id);
          }}
          onMapEditLoaded={(error, svg) => {
            if (!svg) return;
            svgRef.current = svg;
            isSvgLoaded.true();
            onMapReadyEditing?.(floor, svg, svgMapInteractions);
          }}
          label={floor.name ?? `Floor ${index + 1}`}
          showLabel
        />

        {children}
      </div>
      {showMiniMap && !isMapEditing && (
        <MapViewSvgMiniMap mapInteractor={svgMapInteractions} />
      )}
    </>
  );
};
